// bingo-render.js — subscribes to `bingocall` and updates every on-page board,
// ball, call-text, and history strip. Source-agnostic: the detail comes from
// either the real engine (demo-source.js) or the offline loop (bingo-fallback.js).

import { letterFor } from './demo-state.js';

(function () {
  'use strict';

  function applyToBoards(state) {
    document.querySelectorAll('[data-bingo-board]').forEach(function (board) {
      board.querySelectorAll('.cell[data-cell-num]').forEach(function (cell) {
        var n = parseInt(cell.getAttribute('data-cell-num'), 10);
        cell.classList.remove('cell--called', 'cell--current');
        if (state.phase === 'off') return;
        if (n === state.current) cell.classList.add('cell--current');
        else if (state.called.indexOf(n) !== -1) cell.classList.add('cell--called');
      });
    });
  }

  function applyToBalls(state) {
    if (state.current == null) return;
    document.querySelectorAll('[data-bingo-ball]').forEach(function (ball) {
      var letter = state.currentLetter;
      ball.className = ball.className.replace(/ball--[binog]/, '').trim() + ' ball--' + letter.toLowerCase();
      var letterEl = ball.querySelector('.ball__inner-letter');
      var numEl = ball.querySelector('.ball__inner-num');
      if (letterEl) letterEl.textContent = letter;
      if (numEl) numEl.textContent = state.current;
    });
  }

  function applyToCallText(state) {
    if (state.current == null) return;
    document.querySelectorAll('[data-bingo-calltext]').forEach(function (el) {
      el.textContent = state.currentLetter + ' ' + state.current;
    });
    document.querySelectorAll('[data-bingo-callnumber]').forEach(function (el) {
      el.textContent = 'CALL ' + state.callNumber + ' OF ' + state.total;
    });
  }

  var HIST_MOVE_MS = 420;
  var histReducedMotion = typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function historyItemHtml(n) {
    var letter = letterFor(n);
    return '<div class="history-item history-item--' + letter.toLowerCase() + '" data-call="' + n + '">' +
             '<span class="history-item__letter">' + letter + '</span>' +
             '<span class="history-item__num">' + n + '</span>' +
           '</div>';
  }

  function applyToHistory(state) {
    document.querySelectorAll('[data-bingo-history]').forEach(function (container) {
      var max = parseInt(container.getAttribute('data-bingo-history-max') || '4', 10);
      var items = state.history.slice(0, max);

      // Default strips just re-render newest-first (the long-standing behavior).
      if (!container.hasAttribute('data-bingo-history-animate')) {
        container.innerHTML = items.map(historyItemHtml).join('');
        return;
      }

      // Animated strip: a new chip pushes in from the left, every existing chip
      // slides one stride to the right, and the overflow chip slides out under the
      // right-edge fade (horizontal FLIP — mirrors the call-log motion).
      var liveFirst = container.querySelector('.history-item[data-call]');
      var prevNewest = liveFirst ? liveFirst.getAttribute('data-call') : null;
      var newestNow = items.length ? String(items[0]) : null;

      if (prevNewest === null || !items.length) {
        container.innerHTML = items.map(historyItemHtml).join('');
        return;
      }
      if (prevNewest === newestNow) return; // nothing new since last paint

      // Chips prepended since the last paint (usually one).
      var fresh = [];
      for (var i = 0; i < items.length; i++) {
        if (String(items[i]) === prevNewest) break;
        fresh.push(items[i]);
      }
      // Not a clean prepend (game reset / big jump) or reduced motion → resync flat.
      if (!fresh.length || fresh.length >= max || histReducedMotion) {
        container.innerHTML = items.map(historyItemHtml).join('');
        return;
      }

      var gapStr = getComputedStyle(container).columnGap || getComputedStyle(container).gap || '0';
      var gap = parseFloat(gapStr) || 0;
      var stride = liveFirst.offsetWidth + gap;
      var delta = stride * fresh.length;
      container.insertAdjacentHTML('afterbegin', fresh.map(historyItemHtml).join(''));

      var rows = container.children, r, row;
      for (r = 0; r < rows.length; r++) {
        row = rows[r];
        row.style.transition = 'none';
        row.style.transform = 'translateX(' + (-delta) + 'px)';
        if (r < fresh.length) row.style.opacity = '0';
      }
      void container.offsetHeight; // commit the start state before releasing
      for (r = 0; r < rows.length; r++) {
        row = rows[r];
        row.style.transition = 'transform ' + HIST_MOVE_MS + 'ms ease, opacity ' + HIST_MOVE_MS + 'ms ease';
        row.style.transform = '';
        row.style.opacity = '';
      }
      // Dispose any chip now past the max once the slide completes.
      setTimeout(function () {
        while (container.children.length > max) container.removeChild(container.lastElementChild);
        for (var c = 0; c < container.children.length; c++) container.children[c].style.transition = '';
      }, HIST_MOVE_MS + 60);
    });
  }

  // Call log — timestamped rows, newest first ([data-bingo-calllog], optional
  // data-bingo-calllog-max). Derived from state.called (the full call list, unlike
  // the depth-capped state.history). Each call is stamped with the wall-clock time
  // its bingocall event arrived; calls already on the board at page load are
  // backfilled at a plausible cadence. Stamps reset when a new game starts.
  //
  // Motion (FLIP): new calls are prepended and every row — new and old, text and
  // divider — slides down from its previous position in one continuous move. The
  // container is a fixed-height, overflow-hidden viewport (CSS .fg-log), so the
  // row pushed past the max slides out under the bottom fade and is disposed once
  // the move completes — and the fade itself never moves as rows accumulate.
  var logStamps = {};
  var logLastCallNumber = -1;
  var LOG_BACKFILL_MS = 4000; // ≈ the demo engine's observed call cadence
  var LOG_MOVE_MS = 450;
  var logReducedMotion = typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function logTimeText(ms) {
    var d = new Date(ms);
    var h = d.getHours() % 12 || 12;
    var m = ('0' + d.getMinutes()).slice(-2);
    var s = ('0' + d.getSeconds()).slice(-2);
    return h + ':' + m + ':' + s;
  }

  function logRowHtml(n) {
    return '<div class="fg-log__row" data-call="' + n + '">' +
             '<span>' + letterFor(n) + ' ' + n + '</span>' +
             '<span>' + logTimeText(logStamps[n]) + '</span>' +
           '</div>';
  }

  function applyToCallLog(state) {
    var els = document.querySelectorAll('[data-bingo-calllog]');
    if (!els.length) return;
    if (state.phase === 'off') return;
    if (state.callNumber < logLastCallNumber) logStamps = {};
    logLastCallNumber = state.callNumber;

    var newestFirst = state.called.slice().reverse();
    var now = Date.now();
    newestFirst.forEach(function (n, i) {
      if (logStamps[n] == null) logStamps[n] = now - i * LOG_BACKFILL_MS;
    });

    els.forEach(function (el) {
      var max = parseInt(el.getAttribute('data-bingo-calllog-max') || '6', 10);
      var items = newestFirst.slice(0, max);
      if (!items.length) return;

      var liveFirst = el.querySelector('.fg-log__row[data-call]');
      var prevNewest = liveFirst ? liveFirst.getAttribute('data-call') : null;

      // First live paint (replaces the static fallback rows): no motion. The
      // container is a fixed-height, overflow-hidden viewport in CSS (.fg-log),
      // so the bottom fade stays geometrically pinned however many rows exist
      // and exit moves can't grow the card.
      if (prevNewest === null) {
        el.innerHTML = items.map(logRowHtml).join('');
        return;
      }

      // Rows that arrived since the last paint (usually one).
      var fresh = [];
      for (var i = 0; i < items.length; i++) {
        if (String(items[i]) === prevNewest) break;
        fresh.push(items[i]);
      }
      if (!fresh.length) return;

      // A whole screenful at once (or reduced motion): resync without animating.
      if (fresh.length >= max || logReducedMotion) {
        el.innerHTML = items.map(logRowHtml).join('');
        return;
      }

      // FLIP: prepend, then start every row at its previous position (one stride
      // up per fresh call; new rows start above the top, faded out) and release.
      var stride = liveFirst.offsetHeight;
      var delta = stride * fresh.length;
      el.insertAdjacentHTML('afterbegin', fresh.map(logRowHtml).join(''));

      var rows = el.children;
      var r, row;
      for (r = 0; r < rows.length; r++) {
        row = rows[r];
        row.style.transition = 'none';
        row.style.transform = 'translateY(' + (-delta) + 'px)';
        if (r < fresh.length) row.style.opacity = '0';
      }
      void el.offsetHeight; // commit the start state before releasing
      for (r = 0; r < rows.length; r++) {
        row = rows[r];
        row.style.transition = 'transform ' + LOG_MOVE_MS + 'ms ease, opacity ' + LOG_MOVE_MS + 'ms ease';
        row.style.transform = '';
        row.style.opacity = '';
      }
      // The overflow row has slid below the frozen container edge — dispose of it.
      setTimeout(function () {
        while (el.children.length > max) el.removeChild(el.lastElementChild);
        for (var c = 0; c < el.children.length; c++) el.children[c].style.transition = '';
      }, LOG_MOVE_MS + 60);
    });
  }

  // Pattern/status containers may not exist yet (added in a later task); these
  // render only if present, so this is safe to ship now.
  function applyToPattern(state) {
    var els = document.querySelectorAll('[data-demo-pattern]');
    if (!els.length) return;
    els.forEach(function (el) {
      if (!state.pattern || !state.pattern.cells) { el.hidden = true; return; }
      el.hidden = false;
      el.setAttribute('data-accent', state.pattern.accent || 'royal');
      if (el.childElementCount !== 25) {
        var frag = document.createDocumentFragment();
        for (var i = 0; i < 25; i++) frag.appendChild(document.createElement('i'));
        el.innerHTML = '';
        el.appendChild(frag);
      }
      var cells = el.children;
      for (var c = 0; c < 25; c++) {
        cells[c].className = state.pattern.cells.charAt(c) === '1' ? 'is-on' : '';
        if (c === 12) cells[c].classList.add('is-free');
      }
    });
  }

  function applyToStatus(state) {
    document.querySelectorAll('[data-demo-status]').forEach(function (el) {
      var host = el.closest('[data-demo-view]') || el.parentElement;
      if (state.phase === 'intermission') {
        var secs = Math.max(1, Math.ceil((state.nextRoundInMs || 0) / 1000));
        var name = state.pattern && state.pattern.name ? state.pattern.name : '';
        el.innerHTML = '<span class="demo-status__label">New game starting in</span>' +
                       '<span class="demo-status__count">' + secs + '</span>' +
                       (name ? '<span class="demo-status__next">Next: ' + name + '</span>' : '');
        el.hidden = false;
        if (host) host.setAttribute('data-demo-phase', 'intermission');
      } else if (state.phase === 'off') {
        el.innerHTML = '<span class="demo-status__label">Live demo paused</span>';
        el.hidden = false;
        if (host) host.setAttribute('data-demo-phase', 'off');
      } else {
        el.hidden = true;
        if (host) host.setAttribute('data-demo-phase', 'calling');
      }
    });
  }

  document.addEventListener('bingocall', function (e) {
    var state = e.detail;
    if (!state) return;
    applyToBoards(state);
    applyToBalls(state);
    applyToCallText(state);
    applyToHistory(state);
    applyToCallLog(state);
    applyToPattern(state);
    applyToStatus(state);
  });
})();
